import { useCallback, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button, Text } from 'rizzui';
import { Loader } from '../../../components/loader/SpinnerLogo';
import { usePrivilege } from '../../../components/priviledge/PriviledgeProvider';
import CustomTable from '../../../components/table/CustomTable';
import useFetch from '../../../hooks/useFetch';
import useFilters from '../../../hooks/useFilters';
import { menuRoutes } from '../../../router/menu';
import { FacilitatorCallsService } from '../../../services/calls/facilitatorCallsService';

const summaryCardClassName = 'rounded-md border border-gray-200 bg-white p-4';

const columnOrder = [
    'studentFullName',
    'trainingActionCode',
    'groupCode',
    'productDates',
    'progressPercentage',
    'trackingLabel',
    'lastContactAt',
    'productStatusName',
    'contractCode',
    'facilitatorName',
    'currentIncidence',
];

const stageLabels: Record<string, string> = {
    start: 'Inicio',
    follow_up: 'Seguimiento',
    end: 'Fin',
    completed: 'Completado',
};

const getStageLabel = (stage: string): string => stageLabels[stage] || stage || 'N/A';

const normalizeValue = (value: any): string => {
    if (value === null || value === undefined || value === '') return '';
    if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') return String(value);
    if (typeof value === 'object') {
        if ('date' in value && typeof value.date === 'string') return value.date;
        if ('name' in value && typeof value.name === 'string') return value.name;
        if ('code' in value && typeof value.code === 'string') return value.code;
    }

    return '';
};

const formatDatePair = (startDate: any, endDate: any): string => {
    const start = normalizeValue(startDate);
    const end = normalizeValue(endDate);

    if (!start && !end) return '-';
    if (start && end) return `${start} - ${end}`;

    return start || end || '-';
};

const FacilitatorTotalCalls = () => {
    const navigate = useNavigate();
    const service = new FacilitatorCallsService();
    const { userCan } = usePrivilege();
    const canViewScheduled = userCan('show_contract_tracking', 'contracts');
    const canViewAssignments = userCan('show_all_system_facilitators', 'user');
    const canViewPendingExtras = userCan('show_all_pending_contract_extras', 'contracts');
    const canViewStudents = userCan('get_students', 'students');
    const { filters, updateFilters, updateFilterOrder, updatePage, updatePageSize } = useFilters({}, [{ field: 'lastContactAt', order: 'desc' }], 1, 25);

    const [response, loading, _error, refetch] = useFetch(useCallback(async () => {
        const apiResponse = await service.listTotalCalls(filters);
        return apiResponse.getResponseData();
    }, [filters]));

    const rows = response?.data ?? [];
    const summary = response?.summary ?? { total: 0, realized: 0, pending: 0, scheduled: 0 };
    const lastPage = response?.lastPage ?? 1;

    const cards = useMemo(() => ([
        { label: 'Total', value: summary.total },
        { label: 'Realizadas', value: summary.realized },
        { label: 'Pendientes', value: summary.pending },
        { label: 'Agendadas', value: summary.scheduled },
    ]), [summary]);

    const orderedData = () => rows.map((row: any) => ({
        id: row.id,
        studentFullName: normalizeValue(row.studentFullName) || 'N/A',
        trainingActionCode: normalizeValue(row.trainingActionCode) || 'N/A',
        groupCode: normalizeValue(row.groupCode) || 'N/A',
        productDates: formatDatePair(row.productStartDate, row.productEndDate),
        progressPercentage: normalizeValue(row.progressPercentage) || '0',
        trackingLabel: normalizeValue(row.latestTrackingClassificationName) || normalizeValue(row.trackingLabel) || 'Pendiente',
        studentId: normalizeValue(row.studentId),
        currentStage: normalizeValue(row.currentStage),
        currentStageLabel: getStageLabel(normalizeValue(row.currentStage)),
        currentStageRealized: Boolean(row.currentStageRealized),
        lastContactAt: normalizeValue(row.lastContactAt) || 'Sin contacto',
        latestScheduledAt: normalizeValue(row.latestScheduledAt),
        productStatusName: normalizeValue(row.productStatusName) || 'N/A',
        contractCode: normalizeValue(row.contractCode) || 'N/A',
        facilitatorName: [normalizeValue(row.facilitatorUserName), normalizeValue(row.facilitatorUserLastName)].filter(Boolean).join(' ') || 'Sin asignar',
        currentIncidence: normalizeValue(row.currentIncidenceCode)
            ? `${normalizeValue(row.currentIncidenceCode)}${normalizeValue(row.currentIncidenceSubject) ? ` - ${normalizeValue(row.currentIncidenceSubject)}` : ''}`
            : 'Sin incidencia',
        raw: row,
    }));

    if (loading && !response) {
        return <Loader height="50vh" />;
    }

    return (
        <div className="space-y-6 px-1 pb-6">
            <div className="grid gap-4 md:grid-cols-4">
                {cards.map((card) => (
                    <div key={card.label} className={summaryCardClassName}>
                        <Text className="text-xs uppercase text-gray-500">{card.label}</Text>
                        <Text className="mt-2 text-2xl font-semibold text-gray-900">{card.value}</Text>
                    </div>
                ))}
            </div>

            <CustomTable
                id="facilitator-total-calls-table"
                columnOrder={columnOrder}
                data={orderedData()}
                isLoading={loading}
                columnsNotShown={[
                    'currentStage',
                    'studentId',
                    'currentStageRealized',
                    'latestScheduledAt',
                    'raw'
                ]}
                overrideColumns={[
                    {
                        key: 'studentFullName',
                        render: (row: any) => {
                            const studentId = row.row.original.studentId;

                            if (studentId && canViewStudents) {
                                return (
                                    <Link to={`${menuRoutes.students.path}/${studentId}/profile${menuRoutes.students.profile.info}`}>
                                        <Text className="font-bold text-primary">{row.row.original.studentFullName}</Text>
                                    </Link>
                                );
                            }

                            return <Text className="font-bold text-primary">{row.row.original.studentFullName}</Text>;
                        },
                    },
                    {
                        key: 'trainingActionCode',
                        render: (row: any) => <div className="max-w-[18rem] whitespace-normal text-sm text-gray-700">{row.row.original.trainingActionCode}</div>,
                    },
                    {
                        key: 'groupCode',
                        render: (row: any) => <Text className="text-sm text-gray-700">{row.row.original.groupCode}</Text>,
                    },
                    {
                        key: 'productDates',
                        columnNameKey: 'productDates',
                        render: (row: any) => <Text className="text-sm text-gray-600">{row.row.original.productDates}</Text>,
                    },
                    {
                        key: 'progressPercentage',
                        render: (row: any) => <Text className="text-sm text-gray-700">{row.row.original.progressPercentage}%</Text>,
                    },
                    {
                        key: 'trackingLabel',
                        render: (row: any) => (
                            <div>
                                <div>{row.row.original.trackingLabel}</div>
                                <div className="text-xs text-gray-500">
                                    {row.row.original.currentStageLabel}
                                    {row.row.original.latestScheduledAt ? ` · Agenda: ${row.row.original.latestScheduledAt}` : ''}
                                </div>
                            </div>
                        ),
                    },
                    {
                        key: 'lastContactAt',
                        render: (row: any) => (
                            <span className={`rounded-full px-2.5 py-1 text-xs font-medium ${row.row.original.currentStageRealized ? 'bg-emerald-100 text-emerald-800' : 'bg-amber-100 text-amber-800'}`}>
                                {row.row.original.lastContactAt}
                            </span>
                        ),
                    },
                    {
                        key: 'productStatusName',
                        render: (row: any) => <Text className="text-sm text-gray-700">{row.row.original.productStatusName}</Text>,
                    },
                    {
                        key: 'contractCode',
                        render: (row: any) => <Text className="font-medium text-gray-900">{row.row.original.contractCode}</Text>,
                    },
                    {
                        key: 'facilitatorName',
                        columnNameKey: 'facilitatorName',
                        render: (row: any) => <Text className="text-sm text-gray-700">{row.row.original.facilitatorName}</Text>,
                    },
                    {
                        key: 'currentIncidence',
                        columnNameKey: 'incidence',
                        render: (row: any) => <div className="max-w-[18rem] whitespace-normal text-sm text-gray-700">{row.row.original.currentIncidence}</div>,
                    },
                ]}
                actions={[

                ]}
                filters={filters}
                updateFilters={updateFilters}
                updateFilterOrder={updateFilterOrder}
                defaultOrder={filters.filter_order || undefined}
                paginationData={{
                    pageSize: filters.limit,
                    currentPage: filters.page,
                    pageCount: lastPage,
                    totalCount: response?.totalRegisters || rows.length,
                    handlePagination: updatePage,
                    handlePerPage: updatePageSize,
                }}
                search={false}
                filter={false}
                toggleFilters={() => {}}
            >
                <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
                    <input
                        value={filters.filter_filters?.search_text || ''}
                        onChange={(event) => updateFilters({ search_text: event.target.value || null })}
                        placeholder="Buscar por alumno, contrato o acción formativa"
                        className="h-10 w-full rounded-md border border-gray-300 px-3 text-sm md:max-w-md"
                    />
                    <div className="flex gap-2">
                        {canViewAssignments && (
                            <Button
                                variant="outline"
                                onClick={() => navigate(menuRoutes.calls.path + menuRoutes.calls.assignments)}
                            >
                                Ver asignaciones
                            </Button>
                        )}
                        {canViewPendingExtras && (
                            <Button
                                variant="outline"
                                onClick={() => navigate(menuRoutes.calls.path + menuRoutes.calls.pendingExtras)}
                            >
                                Ver extras pendientes
                            </Button>
                        )}
                        {canViewScheduled && (
                            <Button
                                variant="outline"
                                onClick={() => navigate(menuRoutes.calls.path + menuRoutes.calls.scheduled)}
                            >
                                Ver agendados
                            </Button>
                        )}
                        <Button variant="outline" onClick={() => refetch()}>
                            Actualizar
                        </Button>
                    </div>
                </div>
            </CustomTable>
        </div>
    );
};

export default FacilitatorTotalCalls;
